/* eslint-disable @next/next/no-img-element */
import YouTube from 'react-youtube'
import getYouTubeID from 'get-youtube-id'
import imageUrlBuilder from '@sanity/image-url'
import client from '@lib/sanity'
import { toSecureEmbedUrl } from '@lib/embedAllowlist'

/**
 * @portabletext/react decides what is a text block by looking for a `children`
 * array, not by checking `_type` (see isPortableTextBlock in
 * @portabletext/toolkit). Studio saved most `iframe` blocks with an empty
 * `children` array, so those nodes are treated as empty paragraphs and never
 * reach the `types.iframe` renderer below — 221 of the 226 embeds in the
 * dataset are stored that way and have been rendering nothing at all.
 *
 * Dropping the empty `children` restores normal type dispatch. Done at render
 * time rather than by migrating the documents, so it is reversible and does
 * not touch the CMS.
 */
export const normaliseEmbeds = (blocks) => {
  if (!Array.isArray(blocks)) {
    return blocks
  }

  return blocks.map((block) => {
    if (!block || block._type !== 'iframe' || !block.children) {
      return block
    }
    // eslint-disable-next-line no-unused-vars
    const { children, markDefs, ...embed } = block
    return embed
  })
}

export const defaultComponents = {
  types: {
    youtube: ({ value }) => {
      const { url } = value
      const id = getYouTubeID(url)
      return (
        <YouTube
          videoId={id}
          iframeClassName="absolute top-0 left-0 h-full w-full"
          className="pb-video relative h-0 w-full max-w-full overflow-hidden bg-slate-50"
        />
      )
    },
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null
      }
      return (
        <div className="not-prose relative">
          <a
            href={imageUrlBuilder(client).image(value).url()}
            target="_blank"
            rel="noreferrer"
          >
            <img
              alt={value.alt || ' '}
              src={imageUrlBuilder(client)
                .image(value)
                .width(826)
                .fit('fillmax')
                .auto('format')
                .url()}
            />
          </a>
        </div>
      )
    },
    iframe: ({ value }) => {
      // Only render embeds from known media providers. CMS content is
      // trusted-ish, but an iframe runs third-party code in the context of
      // the page, so the host is checked rather than assumed. The returned URL
      // is forced to https so that older http:// embeds are not blocked as
      // mixed content.
      const src = toSecureEmbedUrl(value.src)
      if (!src) {
        return null
      }
      return (
        <div className="aspect-w-16 aspect-h-9 bg-slate-50">
          <iframe
            src={src}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      )
    },
  },
}
